import { AlertColor } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps, showTaxCodeCodeField } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege, languages, selectIntegration } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import {
  createDropdownData,
  fetchIdsFromSelectedDropdownValues,
  getListData,
} from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditTaxCodeForm } from './AddEditTaxCode/AddEditTaxCodeForm';

const TaxCodeList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];
  const userPrivileges = useSelector(Privilege) as string[];
  const integration = useSelector(selectIntegration);
  const allowCodeColumn = useMemo(() => showTaxCodeCodeField(integration), [integration]);

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [surchargeOptions, setSurchargeOptions] = useState<{ label: string; value: any }[]>([]);
  const [existingCode, setExistingCode] = useState<string | null>(null);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    const initial = AddEditTaxCodeForm(strings, allowCodeColumn);
    const fields = [...initial];
    (languageList || []).forEach(language => {
      const nameField = { ...initial[0] };
      nameField.field = `name_${language.value}`;
      nameField.label = `${nameField.label} (${language.text})`;
      nameField.responsive = { xs: 12 };
      fields.unshift(nameField);
    });
    return fields
      .filter(item => item.field !== 'name')
      .map(item => {
        if (item.field === 'surchargeResponses') {
          return {
            ...item,
            options: surchargeOptions,
            responsive: { xs: 12 },
          };
        }
        return { ...item, responsive: { xs: 12 } };
      });
  }, [languageList, allowCodeColumn, surchargeOptions]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.taxCode, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    (async () => {
      try {
        const { data, status } = await axiosInstance.get(URLS.surchargeList);
        if (status === 200 && data?.length) {
          setSurchargeOptions(createDropdownData(data, ['id', 'name']));
        }
      } catch {
        // ignore dropdown load failures
      }
    })();
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      setExistingCode(null);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.getTaxCodesById(recordId));
          setExistingCode(data?.code ?? null);
          const container: FormDataModel = {
            percentage: data?.percentage ?? '',
            surchargeResponses: data?.surchargeResponses ?? [],
            ...(allowCodeColumn ? { code: data?.code ?? '' } : {}),
          };
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
          });
          setFormValues(container);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = {
          percentage: '',
          surchargeResponses: [],
          ...(allowCodeColumn ? { code: '' } : {}),
        };
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
        });
        setFormValues(container);
      }
      setDrawerOpen(true);
    },
    [languageList, allowCodeColumn],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
    setExistingCode(null);
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
      }));
      const payload: Record<string, unknown> = {
        percentage: body.percentage,
        localizedAttributes,
        surcharges: fetchIdsFromSelectedDropdownValues(body.surchargeResponses as any),
      };
      if (allowCodeColumn) {
        const raw = body.code;
        payload.code = raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
      } else if (editingId) {
        payload.code = existingCode;
      }
      if (editingId) {
        await axiosInstance.put(URLS.edit_taxCode(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addTaxCode, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.edit_taxCode(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.taxCode_title_list,
      subtitle: strings.taxCode_list_subtitle,
      searchField: 'name',
      searchPlaceholder: 'Search tax codes',
      AddButtonText: strings.taxCode_title_add,
      listApi: URLS.taxCode,
      deleteApi: URLS.edit_taxCode,
      addPrivilege: privileges.add_tax_code,
      editPrivilege: privileges.edit_tax_code,
      deletePrivilege: privileges.delete_tax_code,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'taxCodeName',
          headerName: strings.taxCode_column_name,
          flex: 2,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        ...(allowCodeColumn
          ? [
              {
                field: 'code',
                headerName: strings.taxCode_text_code,
                flex: 1.5,
                sortable: true,
              },
            ]
          : []),
        { field: 'percentage', headerName: strings.taxCode_column_percentage, flex: 2, sortable: true },
        usageTableColumn(row => [
          { count: row.taxGroupCount, singular: 'Tax Group', plural: 'Tax Groups' },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer, allowCodeColumn],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.taxCode_title_edit : strings.taxCode_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_tax_code)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default TaxCodeList;
